import { useEffect, useRef, useState } from 'react';

export function useBoardScroll({
  firstElementBottom,
  rowHeight,
  currentStepIndex,
  boardKey,
}: {
  firstElementBottom: number;
  rowHeight: number;
  currentStepIndex: number;
  boardKey: string;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(currentStepIndex);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return () => {};
    const rows = container.querySelectorAll('[data-step-index]');

    const containerHeight = container.getBoundingClientRect().height;

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute('data-step-index'));
            setActiveIndex(idx);
          }
        }
      },
      {
        root: container,
        rootMargin: `${-1 * (containerHeight - firstElementBottom - rowHeight)}px 0px ${-1 * firstElementBottom}px 0px`,
        threshold: 0.5,
      }
    );
    rows.forEach(row => {
      observer.observe(row);
    });

    return () => observer.disconnect();
  }, [firstElementBottom, rowHeight, boardKey]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const rows = container.querySelectorAll('[data-step-index]');
    const rowToScrollTo = Array.from(rows).find(
      row => row.getAttribute('data-step-index') === currentStepIndex.toString()
    );
    if (rowToScrollTo && rowHeight > 0) {
      const containerRect = container.getBoundingClientRect();
      const rowRect = rowToScrollTo.getBoundingClientRect();
      container.scrollTo({
        top:
          container.scrollTop +
          (rowRect.bottom - containerRect.bottom + firstElementBottom),
        behavior: 'smooth',
      });
    }
  }, [boardKey, currentStepIndex, firstElementBottom, rowHeight]);

  return { scrollContainerRef, activeIndex };
}
